import { Box } from '@mui/material';
import InputMask from 'react-input-mask';
import { KitButton, KitCard } from '../common/MobileKit';
import { protoIcon } from '../common/ProtoIcon';
import { KitAccordion as Accordion, KitField, KitFormBar, KitGrid2 as Grid2, KitInput, KitSelect, KitSeg } from '../common/MobileKitExtras';

/**
 * Formulário de cliente no shell mobile — `prototipos-mobile/clientes/form.html`.
 * Só apresentação: valores, validação (Yup/Formik), CEP e envio vêm de `pages/customers/Form.js`.
 */

const UserIcon = protoIcon('user');
const BuildingIcon = protoIcon('store');
const PhoneIcon = protoIcon('phone');
const PinIcon = protoIcon('pin');
const MailIcon = protoIcon('mail');
const SearchIcon = protoIcon('search');

// `.input-ico` — ícone à esquerda
function InputIco({ icon: Icon, right, children }) {
    return (
        <Box sx={{ position: 'relative' }}>
            {!right && (
                <Box sx={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: 'text.disabled', display: 'inline-flex', pointerEvents: 'none', zIndex: 1 }}>
                    <Icon size={20} />
                </Box>
            )}
            {children}
        </Box>
    );
}

export default function MobileCustomerForm({
    personType,
    onPersonTypeChange,
    showPfTab,
    showPjTab,
    pfFormik,
    pjFormik,
    isSectionOpen,
    onToggleSection,
    loading,
    loadingCep,
    isEdit,
    activityTypes,
    onCnpjChange,
    onCepBlur,
    onCancel,
}) {
    const formik = personType === 'pf' ? pfFormik : pjFormik;
    const segOptions = [
        ...(showPfTab ? [{ value: 'pf', label: 'Pessoa física' }] : []),
        ...(showPjTab ? [{ value: 'pj', label: 'Pessoa jurídica' }] : []),
    ];

    const errorOf = (name) => (formik.touched[name] && formik.errors[name]) || '';

    // Campo de texto/máscara ligado ao formik do tipo ativo.
    const renderInput = (name, props = {}) => {
        const { mask, onChange, sx, ...rest } = props;
        const common = {
            name,
            value: formik.values[name] ?? '',
            onChange: onChange || formik.handleChange,
            error: Boolean(errorOf(name)),
            ...rest,
        };
        if (mask) {
            return (
                <InputMask mask={mask} value={common.value} onChange={common.onChange} onBlur={rest.onBlur} disabled={rest.disabled}>
                    {(inputProps) => <KitInput {...inputProps} {...common} sx={sx} />}
                </InputMask>
            );
        }
        return <KitInput {...common} sx={sx} />;
    };

    const field = (name, label, options = {}) => {
        const { required, wrap, ...inputProps } = options;
        const body = renderInput(name, inputProps);
        return (
            <KitField label={label} required={required} help={errorOf(name) || undefined} error={Boolean(errorOf(name))}>
                {wrap ? wrap(body) : body}
            </KitField>
        );
    };

    return (
        <Box sx={{ fontSize: 14, lineHeight: 1.4 }} component="form" onSubmit={formik.handleSubmit} noValidate>
            <KitCard sx={{ mb: '12px', boxShadow: 'none' }}>
                <KitSeg options={segOptions} value={personType} onChange={onPersonTypeChange} />
            </KitCard>

            <Accordion
                open={isSectionOpen('main')}
                icon={personType === 'pf' ? UserIcon : BuildingIcon}
                title="Dados principais"
                subtitle={personType === 'pf' ? 'Nome e documento' : 'Razão social, CNPJ e inscrições'}
                onToggle={() => onToggleSection('main')}
            >
                {personType === 'pf' ? (
                    <>
                        {field('first_name', 'Nome', { required: true, type: 'text', placeholder: 'Maria' })}
                        {field('last_name', 'Sobrenome', { required: true, type: 'text', placeholder: 'da Silva' })}
                        {field('cpf', 'CPF', { mask: '999.999.999-99', type: 'text', inputMode: 'numeric', placeholder: '000.000.000-00' })}
                        {field('birthdate', 'Data de nascimento', { mask: '99/99/9999', type: 'text', inputMode: 'numeric', placeholder: 'dd/mm/aaaa' })}
                    </>
                ) : (
                    <>
                        {field('trade_name', 'Nome fantasia', { required: true, type: 'text' })}
                        {field('legal_name', 'Razão social', { required: true, type: 'text' })}
                        {field('cnpj', 'CNPJ', { type: 'text', placeholder: '00.000.000/0000-00', onChange: onCnpjChange })}
                        <KitField label="Tipo de atividade" required help={errorOf('activity_type') || undefined} error={Boolean(errorOf('activity_type'))}>
                            <KitSelect
                                name="activity_type"
                                value={formik.values.activity_type ?? ''}
                                onChange={formik.handleChange}
                                sx={errorOf('activity_type') ? { borderColor: 'error.main' } : undefined}
                            >
                                <option value="">Selecione</option>
                                {activityTypes.map((type) => <option key={type.id} value={type.id}>{type.name}</option>)}
                            </KitSelect>
                        </KitField>
                        {field('state_registration', 'Inscrição estadual', { required: true, type: 'text' })}
                        {field('municipal_registration', 'Inscrição municipal', { required: true, type: 'text' })}
                        {field('principal_cnae', 'CNAE principal', { type: 'text' })}
                    </>
                )}
            </Accordion>

            <Accordion
                open={isSectionOpen('contact')}
                icon={PhoneIcon}
                title="Contato"
                subtitle="E-mail e telefone"
                onToggle={() => onToggleSection('contact')}
            >
                {field('email', 'E-mail', {
                    type: 'email',
                    placeholder: 'cliente@email.com',
                    sx: { pl: '44px' },
                    wrap: (body) => <InputIco icon={MailIcon}>{body}</InputIco>,
                })}
                <Grid2>
                    {field('cell_phone', 'Celular', { mask: '(99) 99999-9999', type: 'text', inputMode: 'tel', placeholder: '(11) 90000-0000' })}
                    {field('phone_number', 'Telefone', { mask: '(99) 9999-9999', type: 'text', inputMode: 'tel', placeholder: '(11) 3000-0000' })}
                </Grid2>
            </Accordion>

            <Accordion
                open={isSectionOpen('address')}
                icon={PinIcon}
                title="Endereço"
                subtitle="Opcional — usado para entregas"
                onToggle={() => onToggleSection('address')}
            >
                {field('cep', 'CEP', {
                    mask: '99999-999',
                    type: 'text',
                    inputMode: 'numeric',
                    placeholder: '00000-000',
                    disabled: loadingCep,
                    onBlur: onCepBlur,
                    sx: { pr: '48px' },
                    wrap: (body) => (
                        <Box sx={{ position: 'relative' }}>
                            {body}
                            <Box
                                component="button"
                                type="button"
                                aria-label="Buscar CEP"
                                disabled={loadingCep}
                                onClick={() => onCepBlur({ target: { value: formik.values.cep } })}
                                sx={{
                                    position: 'absolute',
                                    right: '6px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    width: 38,
                                    height: 38,
                                    borderRadius: '10px',
                                    border: 0,
                                    background: 'none',
                                    p: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'primary.main',
                                    cursor: 'pointer',
                                }}
                            >
                                <SearchIcon size={20} />
                            </Box>
                        </Box>
                    ),
                })}
                {loadingCep && (
                    <Box sx={{ fontSize: 12, color: 'text.disabled', m: '-8px 2px 14px', lineHeight: 1.4 }}>Buscando endereço pelo CEP...</Box>
                )}
                {field('address', 'Rua', { type: 'text', disabled: loadingCep })}
                <Grid2>
                    {field('number_address', 'Número', { type: 'text', inputMode: 'numeric' })}
                    {field('complement', 'Complemento', { type: 'text' })}
                </Grid2>
                {field('neighborhood', 'Bairro', { type: 'text', disabled: loadingCep })}
                {field('city', 'Cidade', { type: 'text', disabled: loadingCep })}
            </Accordion>

            <KitFormBar>
                <KitButton onClick={onCancel}>Cancelar</KitButton>
                <KitButton variant="primary" onClick={formik.handleSubmit} disabled={loading}>
                    {loading ? 'Salvando...' : (isEdit ? 'Atualizar cliente' : 'Salvar cliente')}
                </KitButton>
            </KitFormBar>
        </Box>
    );
}
